import React, { useState, useMemo, useEffect } from 'react';
import { Grow, Collapse, Typography, Grid, Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { select } from 'store/toolkit';
import SplashAnimation from './SplashAnimation';
import ClientConfigError from './ClientConfigError';
import Notification from '../Notification';

const useStyles = makeStyles((theme) => ({
  root: {
    background: theme.palette.common.white,
    position: 'fixed',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    padding: '0 20px 40px',
    zIndex: 1501,
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
  },
  '@keyframes shrink': {
    '0%': { transform: 'scale(1)' },
    '100%': { transform: 'scale(0)' },
  },
  shrink: {
    transform: 'scale(0)',
    animation: '$shrink',
    animationDuration: '.15s',
    animationTimingFunction: 'ease',
    animationIterationCount: 1,
  },
  message: {
    maxWidth: 250,
  },
}));

export const SPLASH_PAGE_TEST_ID = 'splash-page';
export const ERROR_MESSAGE =
  'This is taking longer than expected. Please refresh the page to continue.';
export default function SplashPage({ children }) {
  const classes = useStyles();
  const [minElapsed, setMinElapsed] = useState(false);
  const [errorTimeout, setErrorTimeout] = useState(false);
  const duration = useMemo(() => ({ intro: 1, loop: 4 }), []);
  const timeout = useMemo(() => ({ enter: 0, exit: 300 }), []);

  const configSuccess = useSelector(select.config.success);
  const configError = useSelector(select.config.error);
  const appMounted = useSelector(select.config.appMounted);

  const sessionFetchComplete = useSelector(select.session.isComplete);

  const isAppReady = useMemo(
    () => minElapsed && configSuccess && sessionFetchComplete && appMounted,
    [minElapsed, configSuccess, sessionFetchComplete, appMounted]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => setMinElapsed(true), 1000);
    return () => clearTimeout(timeoutId);
  }, []); // timeout minimum splash display time

  useEffect(() => {
    const timeoutId = setTimeout(() => setErrorTimeout(true), 12000);
    return () => clearTimeout(timeoutId);
  }, []); // timeout for error message

  useEffect(() => {
    if (isAppReady) {
      document.body.classList.remove('no-scroll');
    } else {
      document.body.classList.add('no-scroll');
    }

    return () => {
      document.body.classList.remove('no-scroll');
    };
  }, [isAppReady]);

  return (
    <>
      {configSuccess && sessionFetchComplete && children}
      <Grow in={!isAppReady} timeout={timeout} unmountOnExit>
        <Grid
          data-testid="splash-page"
          container
          justifyContent="center"
          alignItems="center"
          classes={{ root: classes.root }}
        >
          <Box classes={{ root: isAppReady ? classes.shrink : null }}>
            {configError ? (
              <ClientConfigError />
            ) : (
              <>
                <SplashAnimation size={100} duration={duration} />
                <Collapse in={errorTimeout} mountOnEnter>
                  <Typography color="error" align="center" className={classes.message}>
                    {ERROR_MESSAGE}
                  </Typography>
                </Collapse>
              </>
            )}
          </Box>
        </Grid>
      </Grow>
      <Notification />
    </>
  );
}

SplashPage.propTypes = {
  children: PropTypes.node.isRequired,
};
